// ============================================================
// COMPLIANCE ENGINE
// ============================================================
// Suspicious Activity Report (SAR) management, regulatory
// reporting, and compliance case lifecycle.
//
// Integrates with:
//   - KYCEngine — customer data for SAR subject details
//   - MonitoringEngine — alerts that trigger SAR creation
//   - EventBus — publishes SARCreated, SARSubmitted, SARStatusChanged
//
// SAR lifecycle: DRAFT → UNDER_REVIEW → SUBMITTED → ACKNOWLEDGED
// ============================================================

let _seq = 0;
function genId(pfx = '') {
  _seq++;
  return `${pfx}${Date.now().toString(36)}${_seq.toString(36).padStart(4, '0')}${Math.random().toString(36).slice(2, 6)}`;
}

// ---- SAR Status Machine ----

const SARStatus = {
  DRAFT: 'DRAFT',
  UNDER_REVIEW: 'UNDER_REVIEW',
  SUBMITTED: 'SUBMITTED',
  ACKNOWLEDGED: 'ACKNOWLEDGED',
};

const SAR_TRANSITIONS = {
  [SARStatus.DRAFT]: [SARStatus.UNDER_REVIEW],
  [SARStatus.UNDER_REVIEW]: [SARStatus.SUBMITTED, SARStatus.DRAFT],
  [SARStatus.SUBMITTED]: [SARStatus.ACKNOWLEDGED],
  [SARStatus.ACKNOWLEDGED]: [],
};

// ---- SAR Case ----

class SARCase {
  constructor({ customerId, alertIds, reason, createdBy }) {
    this.id = genId('sar_');
    this.customerId = customerId;
    this.alertIds = alertIds || [];
    this.reason = reason || '';
    this.status = SARStatus.DRAFT;
    this.narrative = '';
    this.suspiciousActivity = '';
    this.reportingPeriod = null;
    this.amountInvolved = null;
    this.ncaReference = null;
    this.submittedBy = null;
    this.submittedAt = null;
    this.acknowledgedAt = null;
    this.createdBy = createdBy || 'system';
    this.createdAt = new Date().toISOString();
    this.updatedAt = new Date().toISOString();
    this.statusHistory = [{ status: SARStatus.DRAFT, at: this.createdAt, by: this.createdBy }];
  }
}

// ---- Compliance Engine ----

class ComplianceEngine {
  constructor(kycEngine, monitoringEngine, eventBus) {
    this.kyc = kycEngine;
    this.monitoring = monitoringEngine;
    this.eventBus = eventBus || null;
    this.sars = new Map();
  }

  // ---- SAR Lifecycle ----

  createSAR({ customerId, alertIds, reason, createdBy }) {
    if (!customerId) throw new Error('customerId is required');
    if (!reason) throw new Error('reason is required');

    const sar = new SARCase({ customerId, alertIds, reason, createdBy });
    this.sars.set(sar.id, sar);

    if (this.eventBus) {
      this.eventBus.publish({
        type: 'SARCreated',
        entity_id: sar.id,
        data: { sarId: sar.id, customerId, alertIds: sar.alertIds, reason },
      });
    }

    return sar;
  }

  updateSAR(sarId, updates) {
    const sar = this.sars.get(sarId);
    if (!sar) throw new Error(`SAR ${sarId} not found`);
    if (sar.status === SARStatus.SUBMITTED || sar.status === SARStatus.ACKNOWLEDGED) {
      throw new Error(`Cannot update SAR in ${sar.status} status`);
    }

    if (updates.narrative !== undefined) sar.narrative = updates.narrative;
    if (updates.suspiciousActivity !== undefined) sar.suspiciousActivity = updates.suspiciousActivity;
    if (updates.reportingPeriod !== undefined) sar.reportingPeriod = updates.reportingPeriod;
    if (updates.amountInvolved !== undefined) sar.amountInvolved = updates.amountInvolved;
    sar.updatedAt = new Date().toISOString();

    return sar;
  }

  _transition(sar, newStatus, by) {
    const allowed = SAR_TRANSITIONS[sar.status];
    if (!allowed || !allowed.includes(newStatus)) {
      throw new Error(`Cannot transition SAR from ${sar.status} to ${newStatus}`);
    }
    const oldStatus = sar.status;
    sar.status = newStatus;
    sar.updatedAt = new Date().toISOString();
    sar.statusHistory.push({ status: newStatus, at: sar.updatedAt, by: by || 'system' });

    if (this.eventBus) {
      this.eventBus.publish({
        type: 'SARStatusChanged',
        entity_id: sar.id,
        data: { sarId: sar.id, from: oldStatus, to: newStatus, by },
      });
    }
  }

  reviewSAR(sarId, reviewedBy) {
    const sar = this.sars.get(sarId);
    if (!sar) throw new Error(`SAR ${sarId} not found`);
    this._transition(sar, SARStatus.UNDER_REVIEW, reviewedBy);
    return sar;
  }

  submitSAR(sarId, submittedBy) {
    const sar = this.sars.get(sarId);
    if (!sar) throw new Error(`SAR ${sarId} not found`);
    if (!sar.narrative || sar.narrative.trim().length === 0) {
      throw new Error('Cannot submit SAR: narrative is required');
    }
    if (!sar.suspiciousActivity || sar.suspiciousActivity.trim().length === 0) {
      throw new Error('Cannot submit SAR: suspiciousActivity description is required');
    }

    // Must be UNDER_REVIEW to submit
    if (sar.status === SARStatus.DRAFT) {
      this._transition(sar, SARStatus.UNDER_REVIEW, submittedBy);
    }
    this._transition(sar, SARStatus.SUBMITTED, submittedBy);
    sar.submittedBy = submittedBy;
    sar.submittedAt = sar.updatedAt;

    if (this.eventBus) {
      this.eventBus.publish({
        type: 'SARSubmitted',
        entity_id: sar.id,
        data: { sarId: sar.id, customerId: sar.customerId, submittedBy },
      });
    }

    return sar;
  }

  acknowledgeSAR(sarId, ncaReference) {
    const sar = this.sars.get(sarId);
    if (!sar) throw new Error(`SAR ${sarId} not found`);
    if (!ncaReference) throw new Error('NCA reference is required');

    this._transition(sar, SARStatus.ACKNOWLEDGED, 'NCA');
    sar.ncaReference = ncaReference;
    sar.acknowledgedAt = sar.updatedAt;
    return sar;
  }

  // ---- Queries ----

  getSAR(id) { return this.sars.get(id); }
  getAllSARs() { return Array.from(this.sars.values()); }
  getSARsByCustomer(customerId) {
    return Array.from(this.sars.values()).filter(s => s.customerId === customerId);
  }

  // ---- SAR Report Generation ----

  generateSARReport(sarId) {
    const sar = this.sars.get(sarId);
    if (!sar) throw new Error(`SAR ${sarId} not found`);

    // Subject details from KYC
    let subject = null;
    if (this.kyc) {
      const customer = this.kyc.getCustomer(sar.customerId);
      const kycCase = this.kyc.getKYCCase(sar.customerId);
      if (customer) {
        subject = {
          customerId: customer.id,
          legalName: customer.legalName,
          type: customer.type,
          registrationNumber: customer.registrationNumber,
          registeredAddress: customer.registeredAddress,
          riskRating: kycCase?.riskRating || null,
          individuals: customer.individuals ? Array.from(customer.individuals.values()).map(i => ({
            name: i.name,
            dateOfBirth: i.dateOfBirth,
            nationality: i.nationality,
            role: i.role,
          })) : [],
        };
      }
    }

    // Linked alert details from monitoring
    let alertDetails = [];
    if (this.monitoring && sar.alertIds.length > 0) {
      const allAlerts = this.monitoring.alerts || [];
      alertDetails = allAlerts
        .filter(a => sar.alertIds.includes(a.id))
        .map(a => ({
          alertId: a.id,
          type: a.type,
          severity: a.severity,
          status: a.status,
          details: a.details,
          createdAt: a.createdAt,
        }));
    }

    return {
      sarId: sar.id,
      status: sar.status,
      reportType: 'SAR',
      reportDate: new Date().toISOString().split('T')[0],
      reportingInstitution: {
        name: 'CoreBank Ltd',
        firmReference: 'CB-FRN-999999',
        address: '1 Fintech Street, London, EC2A 1BB',
      },
      subject,
      suspiciousActivity: {
        description: sar.suspiciousActivity,
        narrative: sar.narrative,
        reportingPeriod: sar.reportingPeriod,
        amountInvolved: sar.amountInvolved,
      },
      linkedAlerts: alertDetails,
      reason: sar.reason,
      submittedBy: sar.submittedBy,
      submittedAt: sar.submittedAt,
      ncaReference: sar.ncaReference,
      acknowledgedAt: sar.acknowledgedAt,
      createdAt: sar.createdAt,
    };
  }

  // ---- Dashboard ----

  getComplianceDashboard() {
    const allSARs = this.getAllSARs();
    const draft = allSARs.filter(s => s.status === SARStatus.DRAFT);
    const underReview = allSARs.filter(s => s.status === SARStatus.UNDER_REVIEW);
    const submitted = allSARs.filter(s => s.status === SARStatus.SUBMITTED);
    const acknowledged = allSARs.filter(s => s.status === SARStatus.ACKNOWLEDGED);

    // Overdue: DRAFT or UNDER_REVIEW older than 14 days
    const fourteenDaysAgo = new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString();
    const overdue = allSARs.filter(s =>
      (s.status === SARStatus.DRAFT || s.status === SARStatus.UNDER_REVIEW) &&
      s.createdAt < fourteenDaysAgo
    );

    // By customer risk rating
    const byRisk = { HIGH: 0, MEDIUM: 0, LOW: 0, UNKNOWN: 0 };
    for (const sar of allSARs) {
      if (this.kyc) {
        const kycCase = this.kyc.getKYCCase(sar.customerId);
        const rating = kycCase?.riskRating || 'UNKNOWN';
        byRisk[rating] = (byRisk[rating] || 0) + 1;
      } else {
        byRisk.UNKNOWN++;
      }
    }

    return {
      total: allSARs.length,
      draft: draft.length,
      underReview: underReview.length,
      submitted: submitted.length,
      acknowledged: acknowledged.length,
      overdue: overdue.length,
      byRisk,
      recentSARs: allSARs.slice(-10).reverse(),
    };
  }

  // ---- Regulatory Reports ----

  generateCTR(customerId, periodStart, periodEnd) {
    if (!this.monitoring) return { error: 'MonitoringEngine not available' };

    const customer = this.kyc?.getCustomer(customerId);
    if (!customer) throw new Error(`Customer ${customerId} not found`);

    const actuals = this.monitoring.actuals?.get(customerId);
    const transactions = actuals?.transactions || [];

    const periodTxns = transactions.filter(t => {
      const ts = t.timestamp;
      return ts >= periodStart.toISOString() && ts <= periodEnd.toISOString();
    });

    const inbound = periodTxns.filter(t => t.direction === 'INBOUND');
    const outbound = periodTxns.filter(t => t.direction === 'OUTBOUND');

    return {
      reportType: 'CTR',
      customerId,
      customerName: customer.legalName,
      periodStart: periodStart.toISOString().split('T')[0],
      periodEnd: periodEnd.toISOString().split('T')[0],
      generatedAt: new Date().toISOString(),
      summary: {
        totalTransactions: periodTxns.length,
        totalInbound: inbound.reduce((s, t) => s + t.amount, 0),
        totalOutbound: outbound.reduce((s, t) => s + t.amount, 0),
        inboundCount: inbound.length,
        outboundCount: outbound.length,
      },
      transactions: periodTxns.map(t => ({
        amount: t.amount,
        direction: t.direction,
        scheme: t.scheme,
        counterparty: t.counterparty,
        timestamp: t.timestamp,
      })),
    };
  }

  getHighRiskCustomerReport() {
    if (!this.kyc) return [];

    const cases = this.kyc.getAllCases();

    const highRisk = cases.filter(c => c.riskRating === 'HIGH');

    return highRisk.map(kycCase => {
      const customer = this.kyc.getCustomer(kycCase.customerId);
      const alerts = this.monitoring
        ? (this.monitoring.getAlerts?.(kycCase.customerId) || [])
        : [];
      const sars = this.getSARsByCustomer(kycCase.customerId);

      return {
        customerId: kycCase.customerId,
        legalName: customer?.legalName || 'Unknown',
        riskRating: kycCase.riskRating,
        kycStatus: kycCase.status,
        nextReviewDate: kycCase.nextReviewDate,
        openAlerts: alerts.filter(a => a.status === 'OPEN' || a.status === 'UNDER_REVIEW').length,
        totalAlerts: alerts.length,
        activeSARs: sars.filter(s => s.status !== SARStatus.ACKNOWLEDGED).length,
        totalSARs: sars.length,
      };
    });
  }

  getAlertResolutionReport(periodStart, periodEnd) {
    if (!this.monitoring) return { error: 'MonitoringEngine not available' };

    const allAlerts = this.monitoring.alerts || [];

    const periodAlerts = allAlerts.filter(a => {
      return a.createdAt >= periodStart.toISOString() && a.createdAt <= periodEnd.toISOString();
    });

    const closed = periodAlerts.filter(a =>
      a.status === 'CLOSED_FALSE_POSITIVE' || a.status === 'CLOSED_GENUINE'
    );
    const falsePositives = periodAlerts.filter(a => a.status === 'CLOSED_FALSE_POSITIVE');
    const genuine = periodAlerts.filter(a => a.status === 'CLOSED_GENUINE');
    const open = periodAlerts.filter(a => a.status === 'OPEN');
    const escalated = periodAlerts.filter(a => a.status === 'ESCALATED');

    // Average resolution time for closed alerts
    let avgResolutionMs = 0;
    if (closed.length > 0) {
      const totalMs = closed.reduce((sum, a) => {
        if (a.reviewedAt && a.createdAt) {
          return sum + (new Date(a.reviewedAt).getTime() - new Date(a.createdAt).getTime());
        }
        return sum;
      }, 0);
      avgResolutionMs = totalMs / closed.length;
    }

    return {
      reportType: 'ALERT_RESOLUTION',
      periodStart: periodStart.toISOString().split('T')[0],
      periodEnd: periodEnd.toISOString().split('T')[0],
      generatedAt: new Date().toISOString(),
      totalAlerts: periodAlerts.length,
      closedCount: closed.length,
      openCount: open.length,
      escalatedCount: escalated.length,
      falsePositiveCount: falsePositives.length,
      genuineCount: genuine.length,
      closureRate: periodAlerts.length > 0 ? Math.round((closed.length / periodAlerts.length) * 100) : 0,
      falsePositiveRate: closed.length > 0 ? Math.round((falsePositives.length / closed.length) * 100) : 0,
      avgResolutionHours: Math.round(avgResolutionMs / (1000 * 60 * 60) * 10) / 10,
      bySeverity: {
        LOW: periodAlerts.filter(a => a.severity === 'LOW').length,
        MEDIUM: periodAlerts.filter(a => a.severity === 'MEDIUM').length,
        HIGH: periodAlerts.filter(a => a.severity === 'HIGH').length,
        CRITICAL: periodAlerts.filter(a => a.severity === 'CRITICAL').length,
      },
    };
  }
}

export { SARStatus, SAR_TRANSITIONS, SARCase, ComplianceEngine };
